import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import dayjs from "dayjs";
import { useAlerts } from "../AlertsContext";
import { ALERTS_ENABLED } from "../constants";

/**
 * Días completos transcurridos desde la fecha de servicio de la nota.
 * @param {string} serviceDate - ISO 8601
 * @returns {number}
 */
const daysUnsigned = (serviceDate) => Math.max(0, dayjs().diff(dayjs(serviceDate), "day"));

/** Color del chip según la antigüedad: 7+ días crítico, 3+ advertencia. */
const daysChipColor = (days) => (days >= 7 ? "error" : days >= 3 ? "warning" : "default");

/**
 * Dialog prominente de login (una vez por sesión de navegador, controlado por
 * sessionStorage en AlertsContext): lista las notas clínicas sin firmar del
 * usuario en AdvancedMD con paciente, tipo de nota, fecha del encuentro y los
 * días que llevan sin firmarse.
 *
 * "Open alerts panel" abre el panel de notificaciones (la alerta también vive
 * ahí, así sigue disponible después de cerrar el dialog).
 */
const LoginUnsignedNotesDialog = () => {
  const alertsCtx = useAlerts();
  // DEV-ONLY mientras las notas sin firmar sean mock (ver constants.js).
  if (!ALERTS_ENABLED || !alertsCtx) return null;

  const { loginAlert, dismissLoginAlert, openDrawer } = alertsCtx;
  if (!loginAlert) return null;

  const { open, count, notes = [] } = loginAlert;

  const handleViewPanel = () => {
    dismissLoginAlert();
    openDrawer();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // No cerrar por clic afuera: es una alerta importante de login.
        if (reason === "backdropClick") return;
        dismissLoginAlert();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <WarningAmberIcon color="warning" />
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 600, lineHeight: 1.3 }}>
              Unsigned clinical notes in AdvancedMD
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              You have {count} clinical note{count === 1 ? "" : "s"} pending signature
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <List disablePadding>
          {notes.map((note, idx) => {
            const days = daysUnsigned(note.serviceDate);
            return (
              <React.Fragment key={note.noteId}>
                {idx > 0 && <Divider component="li" />}
                <ListItem disableGutters sx={{ py: 1.25, alignItems: "flex-start" }}>
                  <DescriptionOutlinedIcon
                    fontSize="small"
                    sx={{ color: "text.disabled", mt: 0.4, mr: 1.25 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35 }}>
                      {note.noteType}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                      {note.patientName}
                      {note.chartNumber ? ` · ${note.chartNumber}` : ""}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                      Visit date: {dayjs(note.serviceDate).format("MMM D, YYYY")}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color={daysChipColor(days)}
                    label={
                      days === 0
                        ? "Today"
                        : `${days} day${days === 1 ? "" : "s"} unsigned`
                    }
                    sx={{ fontWeight: 600, ml: 1, mt: 0.4 }}
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>

        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 1 }}>
          Please review and sign these notes in AdvancedMD as soon as possible.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleViewPanel} sx={{ textTransform: "none" }}>
          Open alerts panel
        </Button>
        <Button
          variant="contained"
          onClick={dismissLoginAlert}
          sx={{ textTransform: "none" }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginUnsignedNotesDialog;
